import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { Send, X, Clock, CheckCheck } from 'lucide-react';
import { useTheme } from './ThemeContext';

export default function ConversationView({ customer, job, onClose }) {
  const { colors } = useTheme();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [customer?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    if (!customer?.id) return;

    try {
      const { data, error } = await supabaseData
        .from('communications')
        .select('*')
        .eq('customer_id', customer.id)
        .order('sent_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {

    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !customer) return;
    if (!customer.phone) {
      alert('This customer has no phone number on file.');
      return;
    }

    setSending(true);
    const text = newMessage.trim();
    try {
      // Resolve the sending company + user so the API can enforce opt-out and
      // deduct messaging credits.
      const companyId = job?.company_id || customer?.company_id || null;
      let userId = null;
      try {
        const { data: authData } = await supabase.auth.getUser();
        userId = authData?.user?.id || null;
      } catch (_) { /* non-fatal */ }

      // Actually send the SMS via Telnyx/Twilio.
      const resp = await fetch('/api/sms-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: customer.phone,
          message: text,
          customerId: customer.id,
          companyId,
          userId
        })
      });
      const result = await resp.json();
      if (!resp.ok || !result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      // Log the outbound message so it appears in the thread and history.
      // Inbound replies arrive separately via the sms-webhook.
      const { data: sentMsg, error: sendError } = await supabaseData
        .from('communications')
        .insert([{
          customer_id: customer.id,
          job_id: job?.id,
          type: 'sms',
          message: text,
          direction: 'outbound',
          status: 'sent',
          sent_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (sendError) throw sendError;

      setMessages(prev => [...prev, sentMsg]);
      setNewMessage('');
    } catch (error) {
      alert(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: isMobile ? 0 : '2rem'
    }}>
      <div style={{
        background: colors.bgCard,
        borderRadius: isMobile ? 0 : '12px',
        width: isMobile ? '100%' : '600px',
        height: isMobile ? '100%' : '80vh',
        maxHeight: isMobile ? '100%' : '800px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: colors.bgCard
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: colors.text }}>
              {customer?.name || 'Unknown Customer'}
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: colors.textMuted }}>
              {customer?.phone || 'No phone'}
              {job && ` • Job ${job.job_number}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = colors.bgHover}
            onMouseOut={(e) => e.currentTarget.style.background = 'none'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Messages Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem',
          background: colors.bg,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {messages.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              color: colors.textMuted
            }}>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>No messages yet</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
                Start a conversation below
              </p>
            </div>
          )}

          {messages.map((msg, index) => {
            const isOutbound = msg.direction === 'outbound';
            const isMock = msg.status?.includes('mock');
            
            return (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isOutbound ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  alignSelf: isOutbound ? 'flex-end' : 'flex-start'
                }}
              >
                <div style={{
                  background: isOutbound ? colors.accent : colors.bgInput,
                  color: isOutbound ? '#fff' : colors.text,
                  padding: '0.75rem 1rem',
                  borderRadius: '18px',
                  borderBottomRightRadius: isOutbound ? '4px' : '18px',
                  borderBottomLeftRadius: isOutbound ? '18px' : '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  position: 'relative'
                }}>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.95rem',
                    lineHeight: '1.4',
                    wordBreak: 'break-word'
                  }}>
                    {msg.message}
                  </p>
                  
                  {isMock && (
                    <div style={{
                      marginTop: '0.5rem',
                      paddingTop: '0.5rem',
                      borderTop: `1px solid ${isOutbound ? 'rgba(255,255,255,0.3)' : colors.border}`,
                      fontSize: '0.75rem',
                      opacity: 0.7,
                      fontStyle: 'italic'
                    }}>
                      {isOutbound ? '📤 Mock sent' : '📥 Mock response'}
                    </div>
                  )}
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '0.25rem',
                  fontSize: '0.75rem',
                  color: colors.textMuted
                }}>
                  <Clock size={12} />
                  <span>{formatTime(msg.sent_at)}</span>
                  {isOutbound && (
                    <CheckCheck size={12} style={{ color: '#4ecca3' }} />
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{
          padding: '1rem',
          borderTop: `1px solid ${colors.border}`,
          background: colors.bgCard
        }}>
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'flex-end'
          }}>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              disabled={sending}
              style={{
                flex: 1,
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '20px',
                padding: '0.75rem 1rem',
                color: colors.text,
                fontSize: '0.95rem',
                resize: 'none',
                minHeight: '44px',
                maxHeight: '120px',
                outline: 'none',
                fontFamily: 'Archivo, sans-serif'
              }}
              rows={1}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              style={{
                background: newMessage.trim() && !sending ? colors.accent : colors.bgHover,
                border: 'none',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: newMessage.trim() && !sending ? 'pointer' : 'not-allowed',
                color: '#fff',
                transition: 'all 0.2s',
                flexShrink: 0
              }}
            >
              {sending ? (
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid #fff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>
          
          <p style={{
            margin: '0.75rem 0 0 0',
            fontSize: '0.75rem',
            color: colors.textMuted,
            textAlign: 'center'
          }}>
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
